/* eslint-disable @typescript-eslint/no-explicit-any */
import { TableSort, type TableSortValue } from "@src/shared/constants/sort-order.constants";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  notificationTemplateService,
} from "@src/@api-services/notification-template";
import type {
  AddNotificationTemplate,
  INotificationTemplate,
  INotificationTemplateSearchOptions,
} from "../types";

interface NotificationTemplateState {
  data: INotificationTemplate[];
  total: number;
  params: INotificationTemplateSearchOptions;
  selectedTemplate: INotificationTemplate | null;
  loading: boolean;
  detailLoading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getNotificationTemplates = createAsyncThunk(
  "notificationTemplates/getNotificationTemplates",
  async (params: INotificationTemplateSearchOptions, { rejectWithValue }) => {
    try {
      const response =
        await notificationTemplateService.getNotificationTemplates(params);
      return {
        params,
        data: response.data.data.data as INotificationTemplate[],
        total: response.data.data.totalRecord as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch notification templates"
      );
    }
  }
);

export const getNotificationTemplateDetails = createAsyncThunk(
  "notificationTemplates/getNotificationTemplateDetails",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response =
        await notificationTemplateService.getNotificationTemplateDetails(id);
      return {
        ...(response.data.data as INotificationTemplate),
        id,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch notification template details"
      );
    }
  }
);

export const addNotificationTemplate = createAsyncThunk(
  "notificationTemplates/addNotificationTemplate",
  async (data: AddNotificationTemplate, { dispatch, getState, rejectWithValue }) => {
    try {
      const response =
        await notificationTemplateService.addNotificationTemplate(data);
      const state = getState() as any;
      await dispatch(
        getNotificationTemplates(state.notificationTemplates.params)
      );
      return response.data.data as INotificationTemplate;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to add notification template"
      );
    }
  }
);

export const updateNotificationTemplate = createAsyncThunk(
  "notificationTemplates/updateNotificationTemplate",
  async (
    {
      id,
      templateData,
    }: { id: string | number; templateData: Partial<AddNotificationTemplate> },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response =
        await notificationTemplateService.updateNotificationTemplate(
          id,
          templateData
        );
      const state = getState() as any;
      await dispatch(
        getNotificationTemplates(state.notificationTemplates.params)
      );
      return response.data.data as INotificationTemplate;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to update notification template"
      );
    }
  }
);

export const deleteNotificationTemplate = createAsyncThunk(
  "notificationTemplates/deleteNotificationTemplate",
  async (id: string | number, { dispatch, getState, rejectWithValue }) => {
    try {
      await notificationTemplateService.deleteNotificationTemplate(id);
      const state = getState() as any;
      await dispatch(
        getNotificationTemplates(state.notificationTemplates.params)
      );
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to delete notification template"
      );
    }
  }
);

const initialState: NotificationTemplateState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  selectedTemplate: null,
  loading: false,
  detailLoading: false,
  actionLoading: false,
  error: null,
};

export const notificationTemplateSlice = createSlice({
  name: "notificationTemplates",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedNotificationTemplate: (state) => {
      state.selectedTemplate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNotificationTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNotificationTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getNotificationTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getNotificationTemplateDetails.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getNotificationTemplateDetails.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedTemplate = action.payload;
      })
      .addCase(getNotificationTemplateDetails.rejected, (state) => {
        state.detailLoading = false;
        state.selectedTemplate = null;
      })
      .addCase(addNotificationTemplate.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addNotificationTemplate.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(addNotificationTemplate.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateNotificationTemplate.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateNotificationTemplate.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateNotificationTemplate.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteNotificationTemplate.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteNotificationTemplate.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteNotificationTemplate.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

export const { clearError, clearSelectedNotificationTemplate } =
  notificationTemplateSlice.actions;

export default notificationTemplateSlice.reducer;
